"use client";

import { memo, useCallback, useMemo } from "react";
import { FileTextIcon, AlertCircleIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { openGeneratedDocumentInSidebar } from "@/lib/threadDocumentOps";
import { getLiveThreadId } from "@/components/assistant";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { usePdfDocumentStore } from "@/components/pdf/pdfDocumentStore";

/**
 * Thread-wide documents list rendered as a sidebar view inside the existing PDF
 * panel chrome. Scoped to whatever entries are currently in
 * <see cref="useGeneratedDocumentStore"/> — that's already filtered to "documents
 * on this thread" because <c>threadHistoryProvider</c> hydrates the store from the
 * thread's own messages on cold load and the streaming adapter pushes only this
 * turn's events.
 *
 * Click → opens the document in the unified PDF viewer (same path as the inline
 * card body click), and highlights the active one in the list.
 */
export const ThreadDocumentsPanel = memo(function ThreadDocumentsPanel() {
  const { t } = useTranslation();
  const entries = useGeneratedDocumentStore((s) => s.entries);
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);

  const ordered = useMemo(() => {
    return Object.values(entries).slice().sort((a, b) => a.order - b.order);
  }, [entries]);

  const onOpen = useCallback((id: string, title?: string) => {
    void openGeneratedDocumentInSidebar(id, title, getLiveThreadId());
  }, []);

  if (ordered.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-3 text-center text-[11px] text-muted-foreground/80">
        {t("threadDocuments.empty")}
      </div>
    );
  }

  return (
    <ul className="flex h-full flex-col gap-1 overflow-y-auto px-1.5 py-2">
      {ordered.map((entry) => {
        const isActive = entry.documentId === activeDocumentId;
        const title = entry.title ?? entry.suggestedFileName ?? `document-${entry.documentId.slice(0, 8)}`;
        const versionCount = entry.versions?.length ?? 0;
        const showSpinner = entry.status === "rendering" || entry.updatingVersion;
        const showError = entry.status === "error";
        return (
          <li key={entry.documentId}>
            <button
              type="button"
              onClick={() => onOpen(entry.documentId, entry.title)}
              className={cn(
                "flex w-full items-start gap-1.5 rounded-md border border-transparent px-1.5 py-1.5 text-left transition-colors hover:bg-muted/60 cursor-pointer",
                isActive && "border-border bg-muted/60",
              )}
              title={title}
            >
              <span className="mt-0.5 shrink-0">
                {showSpinner ? (
                  <Spinner size="sm" className="text-muted-foreground" />
                ) : showError ? (
                  <AlertCircleIcon className="size-3.5 text-destructive" />
                ) : (
                  <FileTextIcon className="size-3.5 text-primary" />
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[11px] font-medium leading-tight text-foreground">
                  {title}
                </span>
                {versionCount > 1 && (
                  <span className="text-[10px] text-muted-foreground/80">
                    {t("threadDocuments.versionCount", { count: String(versionCount) })}
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
});
